import React, { useState, useCallback, lazy, Suspense } from 'react';
import { useDeals, Deal } from './api/useDeals';
import { useRunDealSeriesGenerator } from './api/useRunDealSeriesGenerator';
import { Env, getAdminApiUrl, ENV_COLORS, Spinner, ErrorBanner, SuccessBanner } from '../shared';
import { formatInMarketTz } from '@/pages/Marketplace/shared/tzHelpers';
import { useAdminFlag } from './featureFlags';
import { RecurringDealsPanel } from './RecurringDealsPanel';

const EditDealModal = lazy(() => import('./EditDealModal'));
const CancelDealModal = lazy(() => import('./CancelDealModal'));

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

function formatDealDate(startTime: string, tz: string | null): string {
  if (!tz) return new Date(startTime).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  return formatInMarketTz(startTime, tz, 'EEE, MMM d');
}

function formatDealTime(startTime: string, endTime: string, tz: string | null): string {
  if (!tz) {
    const start = new Date(startTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    const end = new Date(endTime).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
    return `${start} – ${end} (TZ unset)`;
  }
  const start = formatInMarketTz(startTime, tz, 'h:mm a');
  const end = formatInMarketTz(endTime, tz, 'h:mm a');
  return `${start} – ${end}`;
}

const STATUS_STYLES: Record<Deal['status'], string> = {
  scheduled: 'bg-violet-950/50 border-violet-800/50 text-violet-400',
  active: 'bg-emerald-950/50 border-emerald-800/50 text-emerald-400',
  expired: 'bg-gray-800/50 border-gray-700/50 text-gray-400',
  deleted: 'bg-red-950/50 border-red-800/50 text-red-400',
};

function StatusBadge({ status }: { status: Deal['status'] }) {
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-medium ${STATUS_STYLES[status]}`}>
      {status === 'active' && <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />}
      {status}
    </span>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Deals Tab
// ─────────────────────────────────────────────────────────────────────────────

const PAGE_SIZE = 25;

export function DealsTab({ onRequestCreate }: { onRequestCreate: (env: Env) => void }): React.ReactElement {
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [selectedEnv, setSelectedEnv] = useState<Env>('prod');
  const [page, setPage] = useState(0);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [editingDeal, setEditingDeal] = useState<Deal | null>(null);
  const [cancelingDeal, setCancelingDeal] = useState<Deal | null>(null);
  const [showOccurrences, setShowOccurrences] = useState(false);
  const [view, setView] = useState<'deals' | 'series'>('deals');
  // When set, the list shows only this series' materialized occurrences.
  const [seriesFilter, setSeriesFilter] = useState<Deal | null>(null);
  const debounceRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const activeApiUrl = getAdminApiUrl(selectedEnv);
  // Reads the selected environment's AppConfig, so the pill and the UI agree.
  const RECURRING_DEALS_ENABLED = useAdminFlag('recurring-deals-enabled', selectedEnv);

  const { deals, total, loading, error, refetch } = useDeals({
    search: debouncedSearch,
    page,
    limit: PAGE_SIZE,
    apiUrl: activeApiUrl,
    includeOccurrences: showOccurrences,
    parentDealId: seriesFilter?.id ?? null,
  });

  const totalPages = Math.ceil(total / PAGE_SIZE);

  const handleSearch = useCallback((value: string) => {
    setSearch(value);
    setPage(0);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => setDebouncedSearch(value), 400);
  }, []);

  const handleEnvChange = useCallback((env: Env) => {
    setSelectedEnv(env);
    setPage(0);
  }, []);

  const showSuccess = useCallback((msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(null), 3000);
  }, []);

  const { run: runScheduler, loading: runningScheduler } = useRunDealSeriesGenerator();
  const handleRunScheduler = useCallback(async () => {
    const result = await runScheduler({ environment: selectedEnv });
    if (result) {
      showSuccess(
        result.status === 'skipped'
          ? result.message ?? 'Scheduler skipped the run.'
          : `Scheduler ran — ${result.occurrencesCreated} new occurrence` +
            `${result.occurrencesCreated === 1 ? '' : 's'} created across ` +
            `${result.seriesProcessed} series.` +
            (result.message ? ` ${result.message}` : ''),
      );
      refetch();
    }
  }, [runScheduler, selectedEnv, showSuccess, refetch]);

  return (
    <>
      {/* Toolbar */}
      <div className="flex items-center gap-2 sm:gap-4 mb-6 flex-wrap">
        <div className="relative flex-1 min-w-[160px] max-w-md">
          <svg className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" /></svg>
          <input
            type="text"
            value={search}
            onChange={e => handleSearch(e.target.value)}
            placeholder="Search by title or venue…"
            className="w-full pl-10 pr-4 py-2.5 bg-gray-800 border border-gray-700 rounded-xl text-sm text-white placeholder-gray-500 focus:outline-none focus:border-violet-500 focus:ring-1 focus:ring-violet-500 transition-all"
          />
        </div>
        {!loading && total > 0 && <span className="text-sm text-gray-500">{total} total</span>}

        {/* Env switcher */}
        <div className="flex items-center gap-1.5 p-1 bg-gray-800/80 border border-gray-700 rounded-xl">
          {(['dev', 'staging', 'prod'] as Env[]).map(env => {
            const envApiUrl = getAdminApiUrl(env);
            const isConfigured = Boolean(envApiUrl);
            const isActive = selectedEnv === env;
            return (
              <button
                key={env}
                onClick={() => isConfigured && handleEnvChange(env)}
                title={isConfigured ? undefined : 'Not configured'}
                disabled={!isConfigured}
                className={`px-3 py-1 text-xs font-medium rounded-lg border transition-all capitalize ${
                  isActive
                    ? `${ENV_COLORS[env].pill} border-opacity-100`
                    : isConfigured
                      ? 'text-gray-400 border-transparent hover:text-gray-200 hover:bg-gray-700/60'
                      : 'text-gray-600 border-transparent cursor-not-allowed opacity-40'
                }`}
              >
                {env}
              </button>
            );
          })}
        </div>

        {/* PROD badge */}
        {selectedEnv === 'prod' && (
          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg border text-xs font-semibold tracking-wide ${ENV_COLORS.prod.badge}`}>
            <span className="w-1.5 h-1.5 rounded-full bg-red-400 animate-pulse" />
            PRODUCTION
          </span>
        )}

        {RECURRING_DEALS_ENABLED && (
          <button
            onClick={() => { setView(v => (v === 'deals' ? 'series' : 'deals')); setSeriesFilter(null); setPage(0); }}
            title="Every recurring series with its occurrence counts and next run."
            className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-xl border transition-all ${
              view === 'series'
                ? 'text-violet-200 bg-violet-900/40 border-violet-600'
                : 'text-gray-400 bg-gray-800/60 border-gray-700 hover:text-gray-200 hover:border-gray-600'
            }`}
          >
            <span className="text-base leading-none">📊</span>
            Series monitor
          </button>
        )}

        {RECURRING_DEALS_ENABLED && view === 'deals' && !seriesFilter && (
          <button
            onClick={() => { setShowOccurrences(v => !v); setPage(0); }}
            title="Materialized child occurrences are hidden by default so a series shows once, not once per day."
            className={`flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-xl border transition-all ${
              showOccurrences
                ? 'text-violet-200 bg-violet-900/40 border-violet-600'
                : 'text-gray-400 bg-gray-800/60 border-gray-700 hover:text-gray-200 hover:border-gray-600'
            }`}
          >
            <span className="text-base leading-none">{showOccurrences ? '👁️' : '🙈'}</span>
            Occurrences
          </button>
        )}

        {RECURRING_DEALS_ENABLED && (
          <button
            onClick={handleRunScheduler}
            disabled={runningScheduler}
            title="Materialize the next 14 days of recurring-deal occurrences now."
            className="flex items-center gap-2 px-3 py-2 text-sm font-medium text-violet-300 bg-violet-950/40 border border-violet-800/50 rounded-xl hover:text-white hover:border-violet-500 hover:bg-violet-900/40 transition-all disabled:opacity-50"
          >
            {runningScheduler ? <Spinner size="sm" /> : <span className="text-base leading-none">🔁</span>}
            Run Scheduler
          </button>
        )}

        <button
          onClick={() => onRequestCreate(selectedEnv)}
          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-violet-600 to-pink-600 rounded-xl hover:opacity-90 transition-all"
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" /></svg>
          Create Deal
        </button>
      </div>

      {seriesFilter && (
        <div className="flex items-center gap-3 flex-wrap mb-4 px-4 py-3 bg-violet-950/30 border border-violet-800/50 rounded-xl">
          <span className="text-base leading-none">🔁</span>
          <div className="flex-1 min-w-0">
            <p className="text-sm text-white truncate">
              Occurrences of <span className="font-semibold">{seriesFilter.title}</span>
              <span className="text-gray-400"> · {seriesFilter.business_name}</span>
            </p>
            <p className="text-xs text-gray-400">
              {total} materialized{' '}
              {seriesFilter.recurrence_end_date
                ? `· series ends ${seriesFilter.recurrence_end_date}`
                : '· no end date'}
              {seriesFilter.recurrence_days?.length
                ? ` · repeats ${seriesFilter.recurrence_days.join(', ')}`
                : ''}
            </p>
          </div>
          <button
            onClick={() => { setSeriesFilter(null); setPage(0); }}
            className="px-3 py-1.5 text-xs font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-lg hover:text-white hover:border-gray-600 transition-colors"
          >
            Back to all deals
          </button>
        </div>
      )}

      {successMsg && <SuccessBanner message={successMsg} />}
      {error && <ErrorBanner message={error} />}

      {view === 'series' && activeApiUrl && (
        <RecurringDealsPanel
          env={selectedEnv}
          apiUrl={activeApiUrl}
          search={debouncedSearch}
          onOpenSeries={(id) => {
            // Jump straight from a series row into its occurrences.
            const match = deals.find(d => d.id === id) ?? null;
            setView('deals');
            setPage(0);
            if (match) setSeriesFilter(match);
          }}
        />
      )}

      {view === 'deals' && (<>
        <div className="bg-gray-900 border border-gray-800 rounded-2xl overflow-hidden">
          {/* Header row */}
          <div className="flex items-center gap-4 px-5 py-3 border-b border-gray-800">
            <div className="w-10 flex-shrink-0" />
            <p className="flex-1 text-xs font-semibold text-gray-500 uppercase tracking-wider">Deal</p>
            <p className="hidden sm:block w-36 flex-shrink-0 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date & Time</p>
            <p className="hidden sm:block w-24 flex-shrink-0 text-xs font-semibold text-gray-500 uppercase tracking-wider">Status</p>
            <div className="flex-shrink-0 w-16" />
          </div>

          {loading && (
            <div className="py-16 text-center flex flex-col items-center gap-3">
              <Spinner size="lg" />
              <p className="text-sm text-gray-500">Loading deals…</p>
            </div>
          )}
          {!loading && deals.length === 0 && (
            <div className="py-16 text-center">
              <p className="text-gray-500 text-sm">{search ? 'No deals match your search.' : 'No deals found.'}</p>
            </div>
          )}

          {!loading && deals.map((d, i) => (
            <div
              key={d.id}
              className={`flex items-center gap-3 sm:gap-4 px-4 sm:px-5 py-3 sm:py-4 hover:bg-gray-800/50 active:bg-gray-800/50 transition-colors group ${i < deals.length - 1 ? 'border-b border-gray-800' : ''}`}
            >
              {/* Logo */}
              <div className="w-10 h-10 rounded-xl bg-gray-800 flex items-center justify-center flex-shrink-0 overflow-hidden border border-gray-700">
                {d.business_logo
                  ? <img src={d.business_logo} alt={d.business_name} className="w-full h-full object-cover" />
                  : <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="1.5"><path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>}
              </div>

              {/* Title + venue + mobile date/status */}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5 min-w-0">
                  <p className="text-sm font-medium text-white truncate">{d.title}</p>
                  {RECURRING_DEALS_ENABLED && d.recurrence_days && d.recurrence_days.length > 0 && !d.parent_deal_id && (
                    <button
                      onClick={() => { setSeriesFilter(d); setPage(0); }}
                      title={`Repeats every ${d.recurrence_days.join(', ')}${d.recurrence_end_date ? ` until ${d.recurrence_end_date}` : ' indefinitely'} — click to see its occurrences`}
                      className="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 bg-violet-950/40 border border-violet-800/50 rounded text-[10px] font-medium text-violet-300 hover:border-violet-500 hover:text-violet-100 transition-colors"
                    >
                      🔁 Recurring
                    </button>
                  )}
                  {RECURRING_DEALS_ENABLED && d.parent_deal_id && (
                    <span
                      title="A materialized occurrence of a recurring series."
                      className="flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 bg-gray-800 border border-gray-700 rounded text-[10px] font-medium text-gray-400"
                    >
                      ↳ Occurrence
                    </span>
                  )}
                </div>
                <p className="text-xs text-gray-500 truncate">{d.business_name}</p>
                <div className="sm:hidden flex items-center gap-2 mt-0.5 flex-wrap">
                  <span className="text-xs text-gray-400">{formatDealDate(d.start_time, d.business_timezone)} · {formatDealTime(d.start_time, d.end_time, d.business_timezone)}</span>
                  <StatusBadge status={d.status} />
                </div>
                <p className="text-xs text-gray-600 truncate font-mono hidden sm:block">{d.id}</p>
              </div>

              {/* Date & time */}
              <div className="hidden sm:block w-36 flex-shrink-0">
                <p className="text-xs text-gray-300">{formatDealDate(d.start_time, d.business_timezone)}</p>
                <p className="text-xs text-gray-500">{formatDealTime(d.start_time, d.end_time, d.business_timezone)}</p>
              </div>

              {/* Status */}
              <div className="hidden sm:flex w-24 flex-shrink-0">
                <StatusBadge status={d.status} />
              </div>

              {/* Actions */}
              <div className="flex items-center gap-1.5 flex-shrink-0 sm:opacity-0 sm:group-hover:opacity-100 transition-all">
                <button
                  onClick={() => setEditingDeal(d)}
                  className="px-3 py-1.5 text-xs font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-lg hover:text-white hover:border-violet-500 hover:bg-gray-700 active:bg-gray-700 transition-all"
                >
                  Edit
                </button>
                <button
                  onClick={() => setCancelingDeal(d)}
                  className="px-3 py-1.5 text-xs font-medium text-red-400 bg-gray-800 border border-gray-700 rounded-lg hover:text-red-300 hover:border-red-700 hover:bg-red-950/30 active:bg-red-950/30 transition-all"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        {!loading && total > 0 && (
          <div className="flex items-center justify-between mt-4 px-1">
            <p className="text-xs text-gray-500">
              Showing {page * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE + deals.length, total)} of {total} deals
            </p>
            {totalPages > 1 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="px-3 py-1.5 text-xs font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-lg hover:text-white hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  ← Prev
                </button>
                <span className="text-xs text-gray-500 tabular-nums">
                  Page {page + 1} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="px-3 py-1.5 text-xs font-medium text-gray-300 bg-gray-800 border border-gray-700 rounded-lg hover:text-white hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        )}
      </>)}

      {editingDeal && (
        <Suspense fallback={null}>
          <EditDealModal
            deal={editingDeal}
            environment={selectedEnv}
            onClose={() => setEditingDeal(null)}
            onUpdated={() => {
              setEditingDeal(null);
              showSuccess('Deal updated successfully.');
              refetch();
            }}
          />
        </Suspense>
      )}

      {cancelingDeal && (
        <Suspense fallback={null}>
          <CancelDealModal
            deal={cancelingDeal}
            environment={selectedEnv}
            onClose={() => setCancelingDeal(null)}
            onDeleted={() => {
              setCancelingDeal(null);
              showSuccess('Deal deleted successfully.');
              refetch();
            }}
          />
        </Suspense>
      )}
    </>
  );
}
